import { ReactNode } from "react";
import { LucideIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

type MobileActionSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Hide the title visually but keep it for screen readers */
  srOnlyTitle?: boolean;
  children: ReactNode;
};

/**
 * Bottom sheet used on mobile in place of desktop popovers/dropdown menus.
 * Callers decide when to use it (via useIsMobile) — desktop keeps its popovers.
 */
export const MobileActionSheet = ({ open, onOpenChange, title, srOnlyTitle, children }: MobileActionSheetProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      side="bottom"
      className="rounded-t-2xl p-0 pb-[max(env(safe-area-inset-bottom),0.75rem)] bg-card dark:bg-background max-h-[85dvh] overflow-y-auto"
    >
      <div className="mx-auto mt-2.5 h-1 w-9 rounded-full bg-muted-foreground/30" />
      <SheetTitle
        className={cn(
          "px-5 pt-3 pb-1 text-[13px] font-medium uppercase tracking-wider text-muted-foreground",
          srOnlyTitle && "sr-only"
        )}
      >
        {title}
      </SheetTitle>
      <div className="px-2 pt-1">{children}</div>
    </SheetContent>
  </Sheet>
);

type ActionSheetItemProps = {
  icon?: LucideIcon;
  label: ReactNode;
  onClick: () => void;
  destructive?: boolean;
  selected?: boolean;
  trailing?: ReactNode;
};

export const ActionSheetItem = ({ icon: Icon, label, onClick, destructive, selected, trailing }: ActionSheetItemProps) => (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={cn(
      "w-full flex items-center gap-3 px-4 py-3.5 rounded-lg text-[15px] text-left transition-colors active:bg-muted",
      destructive ? "text-red-500" : "text-foreground",
      selected && "text-blue-600 dark:text-blue-400"
    )}
  >
    {Icon && <Icon className={cn("h-5 w-5 shrink-0", destructive ? "" : "text-muted-foreground", selected && "text-blue-600 dark:text-blue-400")} />}
    <span className="flex-1 min-w-0">{label}</span>
    {trailing}
  </button>
);

export const ActionSheetSeparator = () => <div className="my-1.5 mx-4 h-px bg-border" />;
